import { Link, useNavigate, useParams } from "react-router";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import { useContact, useUpdateContact } from "@/shared/lib/contacts";
import { useScopedHref } from "@/shared/lib/scope";
import { ContactForm } from "./ContactForm";

export function ContactEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const href = useScopedHref();
  const query = useContact(id);
  const update = useUpdateContact(id ?? "");

  if (query.isLoading) {
    return (
      <Section spacing="md">
        <Container size="4xl">
          <p className="type-body-base text-text-muted">Loading…</p>
        </Container>
      </Section>
    );
  }

  if (query.isError) {
    const notFound = query.error.status === 404;
    return (
      <Section spacing="md">
        <Container size="4xl">
          <div className="flex flex-col gap-4">
            <h1 className="type-heading-lg text-text">
              {notFound ? "Contact not found" : "Couldn't load contact"}
            </h1>
            <Button asChild variant="outline">
              <Link to={href("/app/contacts")}>Back to contacts</Link>
            </Button>
          </div>
        </Container>
      </Section>
    );
  }

  const contact = query.data!;
  const fullName = contact.name ?? "";

  return (
    <Section spacing="md">
      <Container size="4xl">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Link
              to={href(`/app/contacts/${contact.id}`)}
              className="type-label-sm tracking-wider uppercase text-text-muted hover:text-text"
            >
              ← Back to {fullName || "contact"}
            </Link>
            <h1 className="type-heading-lg text-text">Edit contact</h1>
          </div>
          <ContactForm
            mode="edit"
            initialContact={contact}
            submitLabel="Save changes"
            pending={update.isPending}
            error={update.error}
            onSubmit={(input) =>
              update.mutate(input, {
                onSuccess: () => navigate(href(`/app/contacts/${contact.id}`)),
              })
            }
          />
        </div>
      </Container>
    </Section>
  );
}
